
/**
 * Module dependencies.
 */

import { ChangeEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { Collapse } from '../../collapse';
import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormControl, FormControlWrapper, FormGroup, Label } from '../../forms/fields-styled-components';
import { Icon } from 'react-components/media';
import { Text } from '../../typography';
import { color, media } from 'react-components/styles';
import { passwordRequirements as defaultPasswordRequirements, password } from '../../../regex';
import { ifProp, theme } from 'styled-tools';
import { isEmpty, isNil } from 'lodash';
import { useField, useFormState } from '@seegno/react-forms';
import { useTranslate } from '../../../i18n';
import eyeClosedIcon from '../../assets/svg/24/eye-closed.svg';
import eyeIcon from '../../assets/svg/24/eye.svg';
import styled, { css } from 'styled-components';
import zxcvbn from 'zxcvbn';

/**
 * Requirements order.
 */

const requirementsOrder = ['lowercase', 'uppercase', 'number', 'size'];

/**
 * Strength color themes.
 */

const strengthColorThemes = {
  score0: color('danger'),
  score1: color('danger'),
  score2: color('gray400'),
  score3: color('success'),
  score4: color('success')
};

/**
 * `Props` type.
 */

type Props = {
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  hasRequirements?: boolean;
  helpText?: ReactNode;
  id?: string;
  label: string;
  name: string;
  passwordRegex?: string;
  passwordRequirements?: any;
  translationNamespace?: string;
};

/**
 * `InputWrapper` styled component.
 */

const InputWrapper = styled.div`
  flex: 1;
`;

/**
 * `RequirementsList` styled component.
 */

const RequirementsList = styled.ul`
  border: 1px solid ${color('gray300')};
  border-radius: 6px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
`;

/**
 * `RequirementsListItem` styled component.
 */

const RequirementsListItem = styled.li<{ isValid: boolean }>`
  align-items: center;
  border-right: 1px solid ${color('gray300')};
  color: ${color('gray500')};
  padding: 20px 4px 16px;
  text-align: center;

  &:last-child {
    border-right: none;
  }

  * {
    display: block;
  }

  ${ifProp('isValid', css`
    color: ${color('primary')};
  `)}
`;

/**
 * `PasswordStrength` styled component.
 */

const PasswordStrength = styled(Text).attrs({ as: 'div', size: 'normal' })<{ level: number }>`
  padding: 0 8px;
  text-transform: uppercase;

  ${({ level }: { level: number }) => css`
    color: ${strengthColorThemes[`score${level}` as keyof typeof strengthColorThemes]};
  `}
`;

/**
 * `ShowHideButton` styled component.
 */

const ShowHideButton = styled.button.attrs({ type: 'button' })`
  appearance: none;
  background-color: transparent;
  border: 0;
  color: ${color('gray500')};
  cursor: pointer;
  display: inline-block;
  height: 24px;
  margin: 10px 11px 10px 0;
  padding: 0;
  transition: color ${theme('animations.defaultTransition')};

  ${media.min('md')`
    margin: 15px 13px 13px 0;
  `}

  &:focus,
  &:hover {
    color: ${color('nightBlue600')};
  }

  &:disabled {
    color: ${color('gray400')};
  }
`;

/**
 * Is valid requirement.
 */

function isValidRequirement(requirement: string, value: string, passwordRequirements?: any): boolean {
  const requirements = passwordRequirements ?? defaultPasswordRequirements;
  const requirementRegex = new RegExp(requirements[requirement]);

  return requirementRegex.test(value);
}

/**
 * Get score.
 */

function getScore(value: string, passwordRegex?: string) {
  if (!new RegExp(passwordRegex ?? password).exec(value)) {
    return -1;
  }

  const { score } = zxcvbn(value);

  return score;
}

/**
 * Export `PasswordInput` component.
 */

export function PasswordInput(props: Props): JSX.Element {
  const {
    className,
    disabled,
    hasRequirements = false,
    helpText,
    id,
    label,
    name,
    passwordRegex,
    passwordRequirements,
    ...rest
  } = props;

  const { translate } = useTranslate();
  const { error, meta, onChange, value, ...fieldProps } = useField(name);
  const { active } = meta ?? {};
  const { alreadySubmitted, isSubmitting } = useFormState();
  const hasError = !!error && alreadySubmitted;
  const isDisabled = disabled || isSubmitting;
  const isFloatingLabel = Boolean(value) || active;
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    onChange(isEmpty(value) ? undefined : value);
  }, [onChange]);

  const [visible, setVisible] = useState(false);
  const toggleVisibility = useCallback(() => {
    setVisible(!visible);
  }, [setVisible, visible]);

  const score = useMemo(() => {
    return getScore(value, passwordRegex);
  }, [passwordRegex, value]);

  return (
    <FormGroup
      className={className}
      disabled={isDisabled}
    >
      <FormControlWrapper
        disabled={isDisabled}
        hasError={hasError}
        hasFocus={active}
      >
        <Label
          disabled={isDisabled}
          htmlFor={id ?? name}
          isFloating={isFloatingLabel}
        >
          {label}
        </Label>

        <InputWrapper>
          <FormControl
            disabled={isDisabled}
            id={id ?? name}
            name={name}
            onChange={handleChange}
            value={value ?? ''}
            variant={'input'}
            {...rest}
            {...fieldProps}
            type={visible ? 'text' : 'password'}
          />
        </InputWrapper>

        {hasRequirements && !isNil(score) && score >= 0 && (
          <PasswordStrength level={score}>
            {translate(`forms.passwordInput.strength.score${score}`)}
          </PasswordStrength>
        )}

        <ShowHideButton
          aria-label={translate(`forms.passwordInput.${visible ? 'hidePassword' : 'showPassword'}`)}
          disabled={isDisabled}
          onClick={toggleVisibility}
        >
          <Icon
            aria-hidden
            icon={visible ? eyeClosedIcon : eyeIcon}
            size={'24px'}
          />
        </ShowHideButton>
      </FormControlWrapper>

      {hasRequirements && (
        <Collapse isOpen={active || value}>
          <RequirementsList>
            {requirementsOrder.map(requirement => (
              <RequirementsListItem
                isValid={isValidRequirement(requirement, value ?? '', passwordRequirements)}
                key={requirement}
              >
                <Text size={'small'}>
                  {translate(`forms.passwordInput.requirements.${requirement}.symbol`)}
                </Text>

                <Text size={'small'}>
                  {translate(`forms.passwordInput.requirements.${requirement}.label`)}
                </Text>
              </RequirementsListItem>
            ))}
          </RequirementsList>
        </Collapse>
      )}

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        isOpen={hasError}
        name={name}
      />
    </FormGroup>
  );
}
